/**
 * DM W5-0a — the type→allowed-parent matrix (c5119 K-B), ONE source for the kernel's warn seam,
 * the containment audit and any client. The project's `dm.containment.mode` decides whether a refused verdict is
 * recorded (warn, default) or refused (deny, W5-7 K12).
 */
import { DM_ITEM_KIND_TYPE, DM_ITEM_KIND_VALUES, isDmItemKind } from './dmItemKind.js';
export const DM_CONTAINMENT_RULES = [
    'parent_not_allowed',
    'kind_missing',
    'owner_missing',
    'area_depth',
    // W5-1 (K2) — the parent is a dm.item without a kind, and some kinded dm.item may hold the child (c5821 B5).
    'parent_unclassified',
    // W5-1 (K1) — a wave under a wave whose own parent is not a dm.epic (epic → wave → sub-wave is the floor).
    'wave_depth',
    // T1-K1 (#3961) — a dm.epic under a sub-area: epics sit under a ROOT area; a sub-area is a topic (`concerns`).
    'epic_root_area',
];
/** Knowledge types: any parent type, but a parent is required (`owner_missing`). */
export const DM_KNOWLEDGE_TYPES = [
    'dm.spec',
    'dm.plan',
    'dm.policy',
    'dm.doc',
    'dm.memory.feedback',
    'dm.memory.reference',
    'dm.memory.log',
];
/**
 * Child → the parents it may sit under. `dm.area` under `dm.area` only one level deep (`area_depth`);
 * `wave` under `wave` only below a dm.epic (`wave_depth`); `dm.epic` under a ROOT `dm.area` only (`epic_root_area`).
 */
export const DM_CONTAINMENT_MATRIX = {
    'dm.development': ['root'],
    'dm.area': ['root', 'dm.area'],
    'dm.epic': ['dm.area'],
    'dm.item/wave': ['dm.epic', 'dm.item/wave'],
    'dm.item/slice': ['dm.item/wave'],
    'dm.item/container': ['root', 'dm.area', 'dm.epic'],
    'dm.item/standalone': ['dm.area', 'dm.epic', 'dm.item/container'],
};
/** Every type the matrix judges; any other type (dm.issue, dm.job, dm.sprint, dm.decision, non-dm) is outside it. */
export const DM_CONTAINMENT_JUDGED_TYPES = [
    'dm.development',
    'dm.area',
    'dm.epic',
    DM_ITEM_KIND_TYPE,
    ...DM_KNOWLEDGE_TYPES,
];
const OK = { ok: true };
const WAVE_SLOT = `${DM_ITEM_KIND_TYPE}/${'wave'}`;
function slotOf(node) {
    return node.type === DM_ITEM_KIND_TYPE ? `${DM_ITEM_KIND_TYPE}/${node.kind ?? ''}` : node.type;
}
export function isDmKnowledgeType(type) {
    return DM_KNOWLEDGE_TYPES.includes(type);
}
/** Matrix rows listing a `dm.item/*` slot — the only children a kinded dm.item may hold. */
const DM_ITEM_HELD_NODES = new Set(Object.entries(DM_CONTAINMENT_MATRIX)
    .filter(([, parents]) => parents.some((slot) => slot.startsWith(`${DM_ITEM_KIND_TYPE}/`)))
    .map(([node]) => node));
/** Kinds some kinded dm.item may hold, in vocabulary order — the child kinds `parent_unclassified` applies to. */
export const DM_ITEM_HELD_KINDS = DM_ITEM_KIND_VALUES.filter((kind) => DM_ITEM_HELD_NODES.has(`${DM_ITEM_KIND_TYPE}/${kind}`));
/** Pure. The parent slots a child's matrix row allows; `null` = no row (a knowledge type, a kind-less dm.item, an unjudged type). */
export function dmContainmentAllowedParents(child) {
    const node = slotOf(child);
    return Object.hasOwn(DM_CONTAINMENT_MATRIX, node) ? DM_CONTAINMENT_MATRIX[node] : null;
}
/** Pure. The verdict reads `parent.parentType` only here: a wave under a wave (`wave_depth`). */
export function dmContainmentReadsGrandparentType(child, parent) {
    return slotOf(child) === WAVE_SLOT && slotOf(parent) === WAVE_SLOT;
}
/**
 * Pure. W5-7b — a child's verdict reads only its parent and grandparent, so a write re-judges only the direct children
 * whose rule reads what it changes: a dm.item's kind → the kinds some kinded dm.item holds; a move → a dm.area's
 * sub-areas (`area_depth`) and epics (`epic_root_area`), or a wave's sub-waves (`wave_depth`). `null` = the write
 * re-classifies no child.
 */
export function dmContainmentRegressibleChildren(seam, written) {
    if (seam === 'kind')
        return written.type === DM_ITEM_KIND_TYPE ? { types: [DM_ITEM_KIND_TYPE], kinds: DM_ITEM_HELD_KINDS } : null;
    if (written.type === 'dm.area')
        return { types: ['dm.area', 'dm.epic'], kinds: null };
    return slotOf(written) === WAVE_SLOT ? { types: [DM_ITEM_KIND_TYPE], kinds: ['wave'] } : null;
}
/**
 * Pure. The sub-wave candidate: a dm.item holding dm.item children under a wave (K1 slot). The kind backfill
 * stamps it `wave` when `wave_depth` allows; a NULL one is counted apart (`unclassifiedSubwave`).
 */
export function isDmItemSubwave(row) {
    return row.parentType === DM_ITEM_KIND_TYPE && row.parentKind === 'wave' && row.itemChildren > 0;
}
/**
 * Pure. A `dm.item` without a vocabulary kind is `kind_missing` whatever its parent; under such a dm.item a child
 * some kinded dm.item may hold is `parent_unclassified` (before `parent_not_allowed`), any other is `parent_not_allowed`.
 */
export function evaluateDmContainment(child, parent) {
    if (isDmKnowledgeType(child.type))
        return parent === null ? { ok: false, rule: 'owner_missing' } : OK;
    if (child.type === DM_ITEM_KIND_TYPE && !isDmItemKind(child.kind))
        return { ok: false, rule: 'kind_missing' };
    const node = slotOf(child);
    if (!Object.hasOwn(DM_CONTAINMENT_MATRIX, node))
        return OK;
    if (parent !== null && parent.type === DM_ITEM_KIND_TYPE && !isDmItemKind(parent.kind) && DM_ITEM_HELD_NODES.has(node)) {
        return { ok: false, rule: 'parent_unclassified' };
    }
    const allowed = DM_CONTAINMENT_MATRIX[node];
    if (!allowed.includes(parent === null ? 'root' : slotOf(parent)))
        return { ok: false, rule: 'parent_not_allowed' };
    if (child.type === 'dm.area' && parent !== null && parent.parentId !== null)
        return { ok: false, rule: 'area_depth' };
    if (child.type === 'dm.epic' && parent !== null && parent.parentId !== null)
        return { ok: false, rule: 'epic_root_area' };
    if (parent !== null && dmContainmentReadsGrandparentType(child, parent) && parent.parentType !== 'dm.epic') {
        return { ok: false, rule: 'wave_depth' };
    }
    return OK;
}
//# sourceMappingURL=dmContainmentMatrix.js.map